import React, { useState, useCallback } from 'react'
import styled from 'styled-components'
import Row, { AutoRow } from 'components/Layout/Row'
import { ModalActions } from 'components/Modal'
import { useTranslation } from 'contexts/Localization'
import { Text, Box, Button, AutoRenewIcon } from 'vvs-uikit'
import { CurrencyLogo } from 'components/Logo'
import LocalBalance from 'components/LocalBalance'
import { usePriceVvsUsdc } from 'state/farms/hooks'
import { Price, Token } from 'vvs-sdk'
import BigNumber from 'bignumber.js'
import { useUserVvsBar } from 'state/xVvs/hooks'
import { fetchVvsBarUserDataAsync } from 'state/xVvs'
import tokens from 'config/constants/tokens'
import { useERC20 } from 'hooks/useContract'
import useVvsBarHandlers from 'views/xVvs/hooks/useVvsBarHandlers'
import { useAppDispatch } from 'state'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { tryParseAmount } from 'state/swap/hooks'
import AprRow from '../BarCard/AprRow'

interface Props {
  inputValue: string
  rate: Price
  stakingToken: Token
  earningToken: Token
  remark: string
  onDismiss: () => void
  onConfirm: () => void
  onCalculatorClick: () => void
  pendingTx: boolean
  isUnstaking: boolean
  apr: number
}

const ConfirmStep = ({
  inputValue,
  rate,
  remark,
  onDismiss,
  onConfirm,
  onCalculatorClick,
  stakingToken,
  earningToken,
  pendingTx,
  isUnstaking,
  apr,
}: Props) => {
  const { t } = useTranslation()
  const { account } = useActiveWeb3React()

  const { onApprove } = useVvsBarHandlers()

  const vvsContract = useERC20(tokens.vvs.address)

  const [requestedApproval, setRequestedApproval] = useState<boolean>()
  const dispatch = useAppDispatch()

  const converted = isUnstaking
    ? tryParseAmount(new BigNumber(inputValue ?? 0).toString(), tokens.xvvs)
        .multiply(rate ?? '1')
        .toSignificant(18)
    : tryParseAmount(new BigNumber(inputValue ?? 0).toString(), tokens.xvvs)
        .divide(rate ?? '1')
        .toSignificant(18)

  const formattedInputValue = new BigNumber(inputValue || 0).toString()

  const vvsPriceUsd = usePriceVvsUsdc()

  const { barData } = useUserVvsBar()
  const { allowance } = barData

  const handleApprove = useCallback(async () => {
    try {
      setRequestedApproval(true)
      await onApprove(vvsContract)
      dispatch(fetchVvsBarUserDataAsync(account))
      setRequestedApproval(false)
    } catch (e) {
      console.error(e)
    }
  }, [onApprove, dispatch, account, vvsContract])

  const isApproveSpending =
    allowance &&
    allowance.isGreaterThan('0') &&
    allowance.isLessThan(new BigNumber(tryParseAmount(inputValue, tokens.xvvs)?.raw.toString() || 0))

  return (
    <>
      <Text small mb={2}>
        {t(isUnstaking ? 'You will unstake' : 'You will stake')}
      </Text>
      <Row>
        <CurrencyLogo size="30px" currency={stakingToken} />
        <Box ml="8px" style={{ flex: 1 }}>
          <Row justify="space-between">
            <Text>{formattedInputValue}</Text>
            <Text>{stakingToken.symbol}</Text>
          </Row>
          {!isUnstaking && (
            <LocalBalance prefix="~ " color="textSubtle" fontSize="9.75px" value={vvsPriceUsd.times(inputValue)} />
          )}
        </Box>
      </Row>
      <Box height="1px" background="#F0F8F9" width="100%" mt="24px" mb="24px" />{' '}
      <Text small mb={2}>
        {t('You will receive')}
      </Text>
      <Row justify="space-between">
        <AutoRow>
          <CurrencyLogo size="24px" currency={earningToken} />
          <Text ml="8px">{converted}</Text>
        </AutoRow>
        <Text>{earningToken.symbol}</Text>
      </Row>
      <Box mt={3}>
        <AprRow apr={apr} text={t('APR at current rates')} small disableTooltip onCalculatorClick={onCalculatorClick} />
      </Box>
      <AutoRow justify="space-between" mt={3} mb={3}>
        <Text color="textSubtle" small>
          {t('Rates')}
        </Text>
        <Text small>1 xVVS = {rate?.toSignificant(18)} VVS</Text>
      </AutoRow>
      <Text color="textSubtle" fontSize="13px" maxWidth="462px">
        {remark}
      </Text>
      <ModalActions>
        <Button variant="secondary" onClick={onDismiss} width="100%">
          {t('Cancel')}
        </Button>
        <Button
          width="100%"
          onClick={isApproveSpending ? handleApprove : onConfirm}
          isLoading={requestedApproval || pendingTx}
          endIcon={requestedApproval || pendingTx ? <AutoRenewIcon spin color="currentColor" /> : null}
        >
          {isApproveSpending ? t('Approve Spending') : t('Confirm')}
        </Button>
      </ModalActions>
    </>
  )
}

export default ConfirmStep
